import { useDriversWithOpenReturns, useReturnsIntakeFilter, useReturnsIntakePagination } from '../../hooks';
import { ReturnsIntakeSearchBar, ReturnsIntakeDescription } from '../../components';
import { childrenReturnsIntakeOpenReturnsBreadcrumbs } from '../../config';
import { LoaderBackdrop, NavigateLinks, Table } from '@/components';
import { getDriversWithOpenReturnsColumns } from '../../config';
import DriverDebtModal from '../../components/DriverDebtModal';
import { ReturnsIntakeFilters } from '../../models';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useSortableData } from '@/hooks';

const DriversWithOpenReturns = () => {
  const navigate = useNavigate();
  const [selectedDriver, setSelectedDriver] = useState<{ driverId: string; driverName: string } | null>(null);

  const { filter, setFilter, initialFilter } = useReturnsIntakeFilter(false, 'BUSINESS-RETURNS-INTAKE-OPEN-RETURNS-FILTERS');
  const { currentPage, pageSize, handlePageChange, handlePageSizeChange } = useReturnsIntakePagination();

  const {
    drivers,
    totalItems,
    totalPages,
    isLoading,
    formatDate,
    formatDuration,
    getStatusColorClass
  } = useDriversWithOpenReturns({
    filters: filter || {},
    page: currentPage,
    pageSize,
  });

  const { sortedData, handleSort, selectedSort } = useSortableData({
    data: drivers || [],
    currentPage,
  });

  const breadcrumbs = useMemo(
    () => childrenReturnsIntakeOpenReturnsBreadcrumbs(navigate),
    [navigate]
  );

  const columns = useMemo(() => getDriversWithOpenReturnsColumns({
    formatDate,
    formatDuration,
    getStatusColorClass,
    onViewShippings: (driverId, driverName) => setSelectedDriver({ driverId, driverName }),
  }), [formatDate, formatDuration, getStatusColorClass]);

  const handleSearch = (filters: ReturnsIntakeFilters) => {
    setFilter(filters);
    handlePageChange(1);
  };

  return (
    <section className='flex flex-col w-full h-full gap-8 mb-12'>
      <NavigateLinks
        actualPage='Conductores con rendiciones pendientes'
        breadcrumbs={breadcrumbs}
      />

      {isLoading && <LoaderBackdrop />}

      <ReturnsIntakeDescription
        title="Conductores con Rendiciones Pendientes"
        description="Listado de conductores que deben pasar por el depósito para devolver los pedidos que no pudieron ser entregados."
        color="#ff7500"
        icon="info"
      />

      <section className='w-full flex flex-col gap-6'>
        <ReturnsIntakeSearchBar
          search={handleSearch}
          initialFilter={initialFilter}
          filter={filter}
          hideStatus={true}
          hideCommerce={true}
          hideDepartments={true}
        />

        <div className='w-full p-5 flex flex-col bg-white rounded-lg shadow-md gap-5'>
          <div className='flex flex-col overflow-y-auto'>
            {drivers?.length > 0 || isLoading ? (
              <Table
                columns={columns}
                data={sortedData}
                isLoading={isLoading}
                skeletonRows={pageSize}
                getRowKey={(row) => row.driverId}
                handleSort={handleSort}
                selectedSort={selectedSort}
                pagination={{
                  totalItems,
                  totalPages,
                  currentPage,
                  rowsPerPage: pageSize,
                  onPageChange: handlePageChange,
                  onRowsPerPageChange: handlePageSizeChange,
                }}
              />
            ) : (
              <span className='text-gray-500 text-sm italic text-center py-10'>
                No se han encontrado conductores con deuda de rendición con los criterios actuales.
              </span>
            )}
          </div>
        </div>
      </section>

      {selectedDriver && (
        <DriverDebtModal
          driverId={selectedDriver.driverId}
          driverName={selectedDriver.driverName}
          onClose={() => setSelectedDriver(null)}
        />
      )}
    </section>
  );
};

export default DriversWithOpenReturns;
